import { useState, useEffect } from 'react'
import { Search, Check, X, Image, Loader2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { reviewService, Review } from '../services/reviewService'

interface ReviewWithPhotos extends Review {
  review_images?: Array<{
    image_id: string
    image_url: string
    is_approved: boolean | null
    created_at: string
  }>
}

const ReviewPhotoModeration = () => {
  const [reviews, setReviews] = useState<ReviewWithPhotos[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [selectedImage, setSelectedImage] = useState<{ url: string; reviewId: string } | null>(null)
  const [moderatingId, setModeratingId] = useState<string | null>(null)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkAction, setBulkAction] = useState<'approve' | 'reject' | null>(null)

  useEffect(() => {
    loadReviews()
  }, [])

  const loadReviews = async () => {
    setLoading(true)
    try {
      const response = await reviewService.getAll()
      // Filter reviews that have images
      const reviewsWithPhotos = response.data.filter((r: any) => 
        r.review_images && r.review_images.length > 0
      ) as ReviewWithPhotos[]
      setReviews(reviewsWithPhotos)
    } catch (error) {
      console.error('Error loading reviews:', error)
      toast.error('Erreur lors du chargement des avis')
    } finally {
      setLoading(false)
    }
  }

  const getFilteredReviews = () => {
    return reviews.filter(review => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const userName = `${review.user_first_name || ''} ${review.user_last_name || ''}`.toLowerCase()
        const comment = (review.comment || '').toLowerCase()
        if (!userName.includes(term) && !comment.includes(term)) {
          return false
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        const hasPending = review.review_images?.some(img => img.is_approved === null)
        const hasApproved = review.review_images?.some(img => img.is_approved === true)
        const hasRejected = review.review_images?.some(img => img.is_approved === false)

        switch (filterStatus) {
          case 'pending':
            return hasPending
          case 'approved':
            return hasApproved && !hasPending
          case 'rejected':
            return !hasApproved && hasRejected
        }
      }

      return true
    })
  }

  const handleModerateImage = async (reviewId: string, imageId: string, approved: boolean) => {
    setModeratingId(`${reviewId}-${imageId}`)
    try {
      // Update review approval status
      await reviewService.updateApproval(reviewId, approved)
      
      // Update local state
      setReviews(prev => prev.map(review => {
        if (review.review_id === reviewId && review.review_images) {
          return {
            ...review,
            review_images: review.review_images.map(img =>
              img.image_id === imageId ? { ...img, is_approved: approved } : img
            )
          }
        }
        return review
      }))

      toast.success(approved ? 'Photo approuvée' : 'Photo rejetée')
    } catch (error) {
      console.error('Error moderating image:', error)
      toast.error("Erreur lors de la modération de l'image")
    } finally {
      setModeratingId(null)
    }
  }

  const handleBulkModerate = async (approved: boolean) => {
    if (selectedIds.size === 0) {
      toast.error('Aucune photo sélectionnée')
      return
    }

    setBulkAction(approved ? 'approve' : 'reject')
    try {
      const promises = Array.from(selectedIds).map(id => {
        const [reviewId] = id.split('|')
        return reviewService.updateApproval(reviewId, approved)
      })

      await Promise.all(promises)

      // Update local state
      setReviews(prev => prev.map(review => {
        if (review.review_images) {
          return {
            ...review,
            review_images: review.review_images.map(img => {
              const key = `${review.review_id}|${img.image_id}`
              return selectedIds.has(key) ? { ...img, is_approved: approved } : img
            })
          }
        }
        return review
      }))

      toast.success(`${selectedIds.size} photo(s) ${approved ? 'approuvée(s)' : 'rejetée(s)'}`)
      setSelectedIds(new Set())
      setBulkMode(false)
    } catch (error) {
      console.error('Error in bulk moderation:', error)
      toast.error('Erreur lors de la modération en masse')
    } finally {
      setBulkAction(null)
    }
  }

  const toggleSelect = (reviewId: string, imageId: string) => {
    const key = `${reviewId}|${imageId}`
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const getImageStatusBadge = (isApproved: boolean | null) => {
    if (isApproved === null) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          En attente
        </span>
      )
    }
    if (isApproved) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Approuvée
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Rejetée
      </span>
    )
  }

  const filteredReviews = getFilteredReviews()
  const pendingCount = reviews.filter(r => r.review_images?.some(img => img.is_approved === null)).length

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modération des photos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gérez les photos soumises dans les avis clients
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200">
            <Image className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {pendingCount} en attente
            </span>
          </div>
          <button
            onClick={() => setBulkMode(!bulkMode)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              bulkMode
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {bulkMode ? 'Quitter le mode masse' : 'Actions groupées'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par client ou commentaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === 'all' && 'Toutes'}
                {status === 'pending' && 'En attente'}
                {status === 'approved' && 'Approuvées'}
                {status === 'rejected' && 'Rejetées'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkMode && selectedIds.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <span className="text-sm font-medium text-primary-700">
            {selectedIds.size} photo(s) sélectionnée(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulkModerate(true)}
              disabled={bulkAction !== null}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {bulkAction === 'approve' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Approuver tout
            </button>
            <button
              onClick={() => handleBulkModerate(false)}
              disabled={bulkAction !== null}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {bulkAction === 'reject' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Rejeter tout
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune photo à modérer</h3>
          <p className="text-sm text-gray-500">
            {filterStatus === 'pending'
              ? 'Toutes les photos ont été modérées.'
              : 'Aucune photo trouvée avec les filtres actuels.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredReviews.map((review) => (
            <div
              key={review.review_id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Review header */}
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-600">
                        {(review.user_first_name?.[0] || '?').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {review.user_first_name} {review.user_last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-4 h-4 ${
                            star <= review.rating ? 'text-yellow-400' : 'text-gray-200'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {review.is_verified_purchase && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Achat vérifié
                      </span>
                    )}
                  </div>
                </div>
                {review.comment && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{review.comment}</p>
                )}
              </div>

              {/* Photos grid */}
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {review.review_images?.map((image) => {
                    const isPending = image.is_approved === null
                    const isSelected = selectedIds.has(`${review.review_id}|${image.image_id}`)

                    return (
                      <div
                        key={image.image_id}
                        className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                          isPending
                            ? 'border-yellow-300'
                            : image.is_approved
                            ? 'border-green-300'
                            : 'border-red-300'
                        } ${bulkMode && isPending ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (bulkMode && isPending) {
                            toggleSelect(review.review_id, image.image_id)
                          }
                        }}
                      >
                        {/* Image */}
                        <div className="aspect-square">
                          <img
                            src={image.image_url}
                            alt="Photo avis"
                            className="w-full h-full object-cover"
                            onClick={(e) => {
                              if (!bulkMode) {
                                e.stopPropagation()
                                setSelectedImage({ url: image.image_url, reviewId: review.review_id })
                              }
                            }}
                          />
                        </div>

                        {/* Status badge */}
                        <div className="absolute top-2 left-2">
                          {getImageStatusBadge(image.is_approved)}
                        </div>

                        {/* Bulk mode checkbox */}
                        {bulkMode && isPending && (
                          <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-primary-600 border-primary-600'
                              : 'bg-white border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        )}

                        {/* Action buttons (non-bulk mode) */}
                        {!bulkMode && isPending && (
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleModerateImage(review.review_id, image.image_id, true)
                              }}
                              disabled={moderatingId === `${review.review_id}-${image.image_id}`}
                              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors disabled:opacity-50"
                              title="Approuver"
                            >
                              {moderatingId === `${review.review_id}-${image.image_id}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleModerateImage(review.review_id, image.image_id, false)
                              }}
                              disabled={moderatingId === `${review.review_id}-${image.image_id}`}
                              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                              title="Rejeter"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedImage({ url: image.image_url, reviewId: review.review_id })
                              }}
                              className="p-2 bg-white text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
                              title="Agrandir"
                            >
                              <Search className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Already moderated - show expand button */}
                        {!isPending && !bulkMode && (
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedImage({ url: image.image_url, reviewId: review.review_id })
                              }}
                              className="p-2 bg-white text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
                              title="Agrandir"
                            >
                              <Search className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt="Photo agrandie"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ReviewPhotoModeration
